// 198 §D — THE FIRM'S ACCOUNTING YEAR: where it starts, and what a valid
// start is. ONE definition read by both planes (the isomorphic shape —
// caseRefPattern, successFee, clientMatters: pure ESM the browser imports and
// the server `require()`s).
//
// The setting is two FirmSettings columns, `accountingYearStartMonth` and
// `accountingYearStartDay`, NOT NULL DEFAULT 1 — so a firm that never sets
// them keeps 1 January and nothing changes for it. The browser resolves the
// pair off the settings object it already holds (the same seam
// `resolveScheduleAxis` reads, src/lib/scheduleAxis.ts); the server's PUT
// refuses a pair that is not a real day.
//
// FEBRUARY HAS 28 DAYS HERE, deliberately: an exercise cannot start on a day
// that exists one year in four — a firm that typed 29 February would have a
// start that vanishes three years out of four. The route refuses it.
//
// THE COMPUTATION of the exercise's bounds lives in src/lib/periodRange.ts
// beside `yearBounds` (198 D.3: "one definition, read by all six consumers"),
// because it prints LOCAL calendar dates through that module's own
// `localYMD` and must never grow a second date printer.

export const ACCOUNTING_YEAR_DEFAULT = Object.freeze({ month: 1, day: 1 });

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** The number of days a start month offers (February: 28, see the header). */
export function daysInStartMonth(month) {
  return Number.isInteger(month) && month >= 1 && month <= 12 ? DAYS_IN_MONTH[month - 1] : 0;
}

/** Is (month, day) a day every year has? */
export function isValidAccountingYearStart(month, day) {
  return Number.isInteger(day) && day >= 1 && day <= daysInStartMonth(month);
}

/**
 * The firm's accounting-year start, off whatever the host holds: the firm
 * settings object (`accountingYearStartMonth` / `accountingYearStartDay`), or
 * anything else — a resolved schedule axis, null — which answers the default.
 * A pair that is not a real day answers the default too, never a half-start.
 * @param {unknown} input
 * @returns {{ month: number, day: number }}
 */
export function resolveAccountingYearStart(input) {
  const o = input && typeof input === 'object' ? /** @type {Record<string, unknown>} */ (input) : null;
  const month = o ? o.accountingYearStartMonth : undefined;
  const day = o ? o.accountingYearStartDay : undefined;
  if (typeof month === 'number' && typeof day === 'number' && isValidAccountingYearStart(month, day)) {
    return { month, day };
  }
  return { ...ACCOUNTING_YEAR_DEFAULT };
}
